import { paramCase } from 'change-case';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
// @mui
import {
  Box,
  Card,
  Table,
  Switch,
  Button,
  Tooltip,
  TableBody,
  Container,
  IconButton,
  TableContainer,
  TablePagination,
  FormControlLabel,
} from '@mui/material';
// routes
import { PATH_DASHBOARD, PATH_SUPPORT } from '../../../../../routes/paths';
// hooks
import useSettings from '../../../../../hooks/useSettings';
import useTable, { emptyRows } from '../../../../../hooks/useTable';

// components
import Page from '../../../../../components/Page';
import Iconify from '../../../../../components/Iconify';
import Scrollbar from '../../../../../components/Scrollbar';
import HeaderBreadcrumbs from '../../../../../components/HeaderBreadcrumbs';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TableSelectedActions,
  TableSkeleton,
} from '../../../../../components/table';
import SupportCategoryTableRow from './UserSupportCategoryTableRow';
import { adminApi } from '../../../../../utils/axios';
import { USER_SUPPORT_CATEGORY } from '../../../../../utils/apiUrl';
import DeleteConfirmation from '../../../../../components/DeleteConfirmation';
import SupportCategoryTableToolbar from './UserSupportCategoryTableToolbar';

const TABLE_HEAD = [{ id: 'category', label: 'Support Category', align: 'left' }, { id: '' }];

export default function UserSupportCategory() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    //
    selected,
    setSelected,
    onSelectRow,
    onSelectAllRows,
    //
    onSort,
    onChangeDense,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable();

  const { themeStretch } = useSettings();
  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [supportCategoryList, setSupportCategoryList] = useState({ count: 0, results: [] });

  const [filterName, setFilterName] = useState('');

  const [filterRole, setFilterRole] = useState('all');
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleFilterName = (filterName) => {
    setFilterName(filterName);
    setPage(0);
  };

  const handleFilterRole = (event) => {
    setFilterRole(event.target.value);
  };

  useEffect(() => {
    getSupportCategory();
  }, [filterName, rowsPerPage, page]);

  const getSupportCategory = async () => {
    try {
      setIsLoading(true);
      const response = await adminApi.get(USER_SUPPORT_CATEGORY, {
        params: {
          search: filterName,
          offset: rowsPerPage * page,
          limit: rowsPerPage,
        },
      });
      setSupportCategoryList(response.data);
      setIsLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const deleteSupportCategory = async (id) => {
    try {
      const response = await adminApi.delete(`${USER_SUPPORT_CATEGORY}${id}/`);
      getSupportCategory();
      setSelectedCategory(null);
      enqueueSnackbar(response?.data?.message || `Data deleted successfuly!`);
      setIsDeletePopupVisible(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleDeleteRows = (selected) => {
    const deleteRows = supportCategoryList.results.filter((row) => !selected.includes(row.id));
    setSelected([]);
  };

  const handleEditRow = (id) => {
    navigate(PATH_DASHBOARD.account.edit(paramCase(id)));
  };

  const denseHeight = dense ? 52 : 72;

  const isNotFound = supportCategoryList.results.length === 0;

  const addNewCategory = () => {
    navigate(PATH_SUPPORT.support.userSupport.supportCategories.new, { state: { isEdit: false } });
  };

  return (
    <Page title="Support Category: List">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Support Category List"
          links={[
            { name: 'Support Category', href: PATH_SUPPORT.support.userSupport.supportCategories.root },
            { name: 'List' },
          ]}
          action={
            <Button onClick={addNewCategory} variant="contained" startIcon={<Iconify icon={'eva:plus-fill'} />}>
              New Category
            </Button>
          }
        />

        <Card>
          <SupportCategoryTableToolbar
            filterName={filterName}
            filterRole={filterRole}
            onFilterName={handleFilterName}
            onFilterRole={handleFilterRole}
          />
          <TableContainer sx={{ minWidth: 800, position: 'relative' }}>
            {selected.length > 0 && (
              <TableSelectedActions
                dense={dense}
                numSelected={selected.length}
                rowCount={supportCategoryList.count}
                onSelectAllRows={(checked) =>
                  onSelectAllRows(
                    checked,
                    supportCategoryList.results.map((row) => row.id)
                  )
                }
                actions={
                  <Tooltip title="Delete">
                    <IconButton color="primary" onClick={() => handleDeleteRows(selected)}>
                      <Iconify icon={'eva:trash-2-outline'} />
                    </IconButton>
                  </Tooltip>
                }
              />
            )}

            <Scrollbar>
              <Table size={dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={supportCategoryList.results.length}
                  numSelected={selected.length}
                  onSort={onSort}
                  onSelectAllRows={(checked) =>
                    onSelectAllRows(
                      checked,
                      supportCategoryList.results.map((row) => row.id)
                    )
                  }
                />
                {isLoading ? (
                  <>
                    <TableSkeleton />
                    <TableSkeleton />
                    <TableSkeleton />
                    <TableSkeleton />
                    <TableSkeleton />
                  </>
                ) : (
                  <TableBody>
                    {supportCategoryList.results
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((row) => (
                        <SupportCategoryTableRow
                          key={row.id}
                          row={row}
                          selected={selected.includes(row.id)}
                          onSelectRow={() => onSelectRow(row.id)}
                          onDeleteRow={() => {
                            setSelectedCategory(row);
                            setIsDeletePopupVisible(true);
                          }}
                          onEditRow={() => handleEditRow(row.name)}
                        />
                      ))}

                    <TableEmptyRows
                      height={denseHeight}
                      emptyRows={emptyRows(page, rowsPerPage, supportCategoryList.count)}
                    />

                    <TableNoData isNotFound={isNotFound} />
                  </TableBody>
                )}
              </Table>
            </Scrollbar>
          </TableContainer>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={supportCategoryList.count}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />

            <FormControlLabel
              control={<Switch checked={dense} onChange={onChangeDense} />}
              label="Dense"
              sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
            />
          </Box>
        </Card>
        <DeleteConfirmation
          isVisible={isDeletePopupVisible}
          onDelete={() => {
            deleteSupportCategory(selectedCategory.id);
          }}
          onCancel={() => setIsDeletePopupVisible(false)}
        />
      </Container>
    </Page>
  );
}

// ----------------------------------------------------------------------

function applySortFilter({ tableData, comparator, filterName, filterStatus, filterRole }) {
  const stabilizedThis = tableData.map((el, index) => [el, index]);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  tableData = stabilizedThis.map((el) => el[0]);

  if (filterName) {
    tableData = tableData.filter((item) => item.name.toLowerCase().indexOf(filterName.toLowerCase()) !== -1);
  }

  if (filterStatus !== 'all') {
    tableData = tableData.filter((item) => item.status === filterStatus);
  }

  if (filterRole !== 'all') {
    tableData = tableData.filter((item) => item.role === filterRole);
  }

  return tableData;
}
